import * as moment from 'moment';
import { utils, write } from 'xlsx';
import type { WorkSheet } from 'xlsx';
import { TranslateService } from '@ngx-translate/core';
import { tr, clearTranslationCache } from '@util/tr';
import { formatUtcTimestampRome } from '@util/utc-timestamp';
import { DWSLanguagesService } from '@services/dws/dws-languages.service';
import { AdvancedExportRow, AdvancedExportScope, DwspayPaymentSummary } from '@services/dws/ws-payments.service';

export type AdvancedExportFormat = 'csv' | 'xlsx';

export interface ExportDateChunk {
  dateFrom: string;
  dateTo: string;
}

export namespace PaymentsAdvancedExport {
  /** Default days per request (server max is 31). Keep moderate to avoid gateway 502. */
  export const ADVANCED_EXPORT_CHUNK_DAYS = 7;

  /** Server hard limit per advanced-export request. */
  export const ADVANCED_EXPORT_MAX_CHUNK_DAYS = 31;

  /** Smallest chunk when adaptive split retries after timeout. */
  export const ADVANCED_EXPORT_MIN_CHUNK_DAYS = 1;

  /** Max chunk requests in flight (parallel). Keep low to avoid gateway 502 / Stripe rate limits. */
  export const ADVANCED_EXPORT_CHUNK_CONCURRENCY = 2;

  /**
   * wspayments parses advanced-export LocalDateTime as UTC wall clock
   * (`toEpochSecond(ZoneOffset.UTC)`). Encode true instants that way so Rome
   * calendar days match the payments list (Unix local midnight bounds).
   */
  export const toWspaymentsUtcWallClock = (instant: moment.Moment): string =>
    instant.clone().utc().format('YYYY-MM-DDTHH:mm:ss');

  /** Inclusive local calendar range → exclusive [from, to) as wspayments UTC-naive strings. */
  export const buildExclusiveExportRange = (
    start: Date | moment.Moment,
    end: Date | moment.Moment,
  ): { dateFrom: string; dateTo: string } => {
    const fromLocal = moment(start).startOf('day');
    const toExclusiveLocal = moment(end).startOf('day').add(1, 'day');
    return {
      dateFrom: toWspaymentsUtcWallClock(fromLocal),
      dateTo: toWspaymentsUtcWallClock(toExclusiveLocal),
    };
  };

  export const buildExportChunks = (
    dateFrom: string,
    dateToExclusive: string,
    chunkDays: number = ADVANCED_EXPORT_CHUNK_DAYS
  ): ExportDateChunk[] => {
    const days = Math.max(ADVANCED_EXPORT_MIN_CHUNK_DAYS, Math.min(ADVANCED_EXPORT_MAX_CHUNK_DAYS, chunkDays));
    // Bounds are UTC wall-clock strings (see toWspaymentsUtcWallClock).
    const start = moment.utc(dateFrom);
    const end = moment.utc(dateToExclusive);
    const chunks: ExportDateChunk[] = [];
    let cursor = start.clone();

    while (cursor.isBefore(end)) {
      const chunkEnd = moment.min(cursor.clone().add(days, 'day'), end);
      chunks.push({
        dateFrom: cursor.format('YYYY-MM-DDTHH:mm:ss'),
        dateTo: chunkEnd.format('YYYY-MM-DDTHH:mm:ss'),
      });
      cursor = chunkEnd;
    }

    return chunks;
  };

  /** Split a chunk in half for adaptive retry after gateway timeout. Returns null if already minimal. */
  export const splitExportChunk = (chunk: ExportDateChunk): [ExportDateChunk, ExportDateChunk] | null => {
    const start = moment.utc(chunk.dateFrom);
    const end = moment.utc(chunk.dateTo);
    if (end.diff(start, 'days', true) <= ADVANCED_EXPORT_MIN_CHUNK_DAYS) {
      return null;
    }

    const midTs = Math.floor((start.valueOf() + end.valueOf()) / 2);
    const mid = moment.utc(midTs);
    if (!mid.isAfter(start) || !end.isAfter(mid)) {
      return null;
    }

    return [
      {
        dateFrom: start.format('YYYY-MM-DDTHH:mm:ss'),
        dateTo: mid.format('YYYY-MM-DDTHH:mm:ss'),
      },
      {
        dateFrom: mid.format('YYYY-MM-DDTHH:mm:ss'),
        dateTo: end.format('YYYY-MM-DDTHH:mm:ss'),
      },
    ];
  };

  /** Succeeded, refunded, and partially refunded only (excludes failed / draft). */
  export const isExportableAdvancedExportRow = (row: AdvancedExportRow): boolean => {
    const status = (row?.status || '').toLowerCase();
    if (status === 'failed') {
      return false;
    }
    if (status === 'succeeded' || status === 'refunded' || status === 'partially_refunded') {
      return true;
    }
    const refunded = Number(row?.amount_refunded) || 0;
    const amount = Number(row?.amount) || 0;
    return refunded > 0 && amount > 0 && refunded <= amount;
  };

  export const mergeExportRows = (chunkResults: AdvancedExportRow[][]): AdvancedExportRow[] => {
    const byStripeId = new Map<string, AdvancedExportRow>();
    for (const rows of chunkResults) {
      for (const row of rows) {
        if (row?.stripe_id && isExportableAdvancedExportRow(row)) {
          byStripeId.set(row.stripe_id, row);
        }
      }
    }
    return Array.from(byStripeId.values()).sort(
      (a, b) => (Number(b.date) || 0) - (Number(a.date) || 0)
    );
  };

  const COLUMN_KEYS: { headerKey: string; field: keyof AdvancedExportRow }[] = [
    { headerKey: 'payments.advancedExport.col.stripeId', field: 'stripe_id' },
    { headerKey: 'payments.advancedExport.col.date', field: 'date' },
    { headerKey: 'payments.advancedExport.col.status', field: 'status' },
    { headerKey: 'payments.advancedExport.col.amount', field: 'amount' },
    { headerKey: 'payments.advancedExport.col.amountRefunded', field: 'amount_refunded' },
    { headerKey: 'payments.advancedExport.col.currency', field: 'currency' },
    { headerKey: 'payments.advancedExport.col.totalFee', field: 'total_fee' },
    { headerKey: 'payments.advancedExport.col.wsPaymentsFee', field: 'wspayments_fee' },
    { headerKey: 'payments.advancedExport.col.divineaFee', field: 'divinea_fee' },
    { headerKey: 'payments.advancedExport.col.description', field: 'description' },
    { headerKey: 'payments.advancedExport.col.customerName', field: 'customer_name' },
    { headerKey: 'payments.advancedExport.col.customerEmail', field: 'customer_email' },
    { headerKey: 'payments.advancedExport.col.saleId', field: 'sale_id' },
    { headerKey: 'payments.advancedExport.col.saleType', field: 'entity_type' },
    { headerKey: 'payments.advancedExport.col.transactionMode', field: 'transaction_mode' },
  ];

  const PAYOUT_COLUMN_KEYS: { headerKey: string; field: keyof AdvancedExportRow }[] = [
    { headerKey: 'payments.advancedExport.col.payoutId', field: 'payout_id' },
    { headerKey: 'payments.advancedExport.col.payoutAmount', field: 'payout_amount' },
    { headerKey: 'payments.advancedExport.col.payoutDate', field: 'payout_date' },
  ];

  const MONEY_FIELDS = new Set<keyof AdvancedExportRow>([
    'amount',
    'amount_refunded',
    'total_fee',
    'wspayments_fee',
    'divinea_fee',
    'payout_amount',
  ]);

  /** Excel number format so values in the 1–999 range are not shown as dates. */
  const MONETARY_EXCEL_NUMFMT = '#,##0.00';

  const columnsForScope = (_exportScope: AdvancedExportScope = 'payments') =>
    [...COLUMN_KEYS, ...PAYOUT_COLUMN_KEYS];

  const buildFilename = (ext: AdvancedExportFormat = 'csv'): string => {
    const ts = moment().format('YYYYMMDDHHmmss');
    return `WS_Payments_export_${ts}.${ext}`;
  };

  const escapeCsv = (value: unknown): string => {
    const text = value == null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const formatMoney = (cents: unknown): string => {
    if (cents == null || cents === '') {
      return '';
    }
    const num = typeof cents === 'number' ? cents : Number(cents);
    if (Number.isNaN(num)) {
      return '';
    }
    return (num / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatFee = (cents: unknown): string => {
    const formatted = formatMoney(cents);
    if (!formatted) {
      return formatted;
    }
    const num = typeof cents === 'number' ? cents : Number(cents);
    if (!num) {
      return formatted;
    }
    return formatted.startsWith('-') ? formatted : `-${formatted}`;
  };

  const formatDateUtc = (epochSeconds: unknown): string => {
    if (epochSeconds == null || epochSeconds === '') {
      return '';
    }
    const num = typeof epochSeconds === 'number' ? epochSeconds : Number(epochSeconds);
    if (Number.isNaN(num)) {
      return '';
    }
    return formatUtcTimestampRome(new Date(num * 1000));
  };

  const translateStatus = (status: string): string => {
    switch (status) {
      case 'succeeded':
        return tr('payments.advancedExport.status.succeeded');
      case 'failed':
        return tr('payments.advancedExport.status.failed');
      case 'refunded':
        return tr('payments.advancedExport.status.refunded');
      case 'partially_refunded':
        return tr('payments.advancedExport.status.partiallyRefunded');
      default:
        return status || '';
    }
  };

  const translateEntityType = (entityType: string): string => {
    switch (entityType) {
      case 'reservation':
        return tr('payments.advancedExport.saleType.reservation');
      case 'order':
        return tr('payments.advancedExport.saleType.order');
      case 'preorder':
        return tr('payments.list.entityPreorder');
      case 'gift':
        return tr('payments.list.entityGift');
      case 'mixed':
      case 'mixed_sale':
        return tr('payments.advancedExport.saleType.mixed');
      default:
        return entityType || '';
    }
  };

  const translateTransactionMode = (mode: string): string => {
    switch (mode) {
      case 'card_present':
        return 'Card Present';
      case 'card_not_present':
        return 'Card Not Present';
      default:
        return mode || '';
    }
  };

  const formatCell = (row: AdvancedExportRow, field: keyof AdvancedExportRow): string => {
    const value = row[field];
    switch (field) {
      case 'date':
        return formatDateUtc(value);
      case 'status':
        return translateStatus(String(value || ''));
      case 'amount':
      case 'amount_refunded':
        return formatMoney(value);
      case 'total_fee':
      case 'wspayments_fee':
      case 'divinea_fee':
        return formatFee(value);
      case 'entity_type':
        return translateEntityType(String(value || ''));
      case 'transaction_mode':
        return translateTransactionMode(String(value || ''));
      case 'payout_amount':
        return formatMoney(value);
      case 'payout_date':
        return formatDateUtc(value);
      default:
        return value == null ? '' : String(value);
    }
  };

  const toMoneyNumber = (cents: unknown): number | '' => {
    if (cents == null || cents === '') {
      return '';
    }
    const num = typeof cents === 'number' ? cents : Number(cents);
    if (Number.isNaN(num)) {
      return '';
    }
    return num / 100;
  };

  const formatXlsxCell = (row: AdvancedExportRow, field: keyof AdvancedExportRow): string | number => {
    if (MONEY_FIELDS.has(field)) {
      const money = toMoneyNumber(row[field]);
      return money === '' ? '' : money;
    }
    return formatCell(row, field);
  };

  const applyMonetaryNumberFormat = (sheet: WorkSheet, moneyColIndexes: number[]): void => {
    if (!sheet['!ref'] || !moneyColIndexes.length) {
      return;
    }
    const range = utils.decode_range(sheet['!ref']);
    for (const c of moneyColIndexes) {
      if (c < range.s.c || c > range.e.c) {
        continue;
      }
      for (let r = range.s.r + 1; r <= range.e.r; r++) {
        const addr = utils.encode_cell({ r, c });
        const cell = sheet[addr];
        if (cell && cell.t === 'n' && typeof cell.v === 'number' && Number.isFinite(cell.v)) {
          cell.z = MONETARY_EXCEL_NUMFMT;
        }
      }
    }
  };

  const sortedExportRows = (rows: AdvancedExportRow[]): AdvancedExportRow[] =>
    [...rows].sort((a, b) => (b.date || 0) - (a.date || 0));

  const normalizeExportSaleId = (id?: string | null): string => {
    const value = (id ?? '').trim();
    const lower = value.toLowerCase();
    if (
      !value
      || lower === 'draft'
      || lower === 'manual-payment-link'
      || lower === 'manual-insert'
      || lower === 'manual-insert-moto'
    ) {
      return '';
    }
    return value;
  };

  /** WS-3825: same as wspayments {@code PaymentMetadataLogic.resolveSearchableSaleId}. */
  const resolveExportSaleIdFromMetadata = (metadata: Record<string, string | undefined>): string => {
    const entityId = normalizeExportSaleId(metadata.entity_id ?? metadata.entityId);
    if (entityId) {
      return entityId;
    }
    return normalizeExportSaleId(metadata.sale_id ?? metadata.saleId);
  };

  const LIST_EXPORT_PENDING_STATUSES = new Set([
    'charged',
    'requires_capture',
    'requires_action',
    'requires_payment_method',
    'requires_confirmation',
    'opened',
    'processing',
    'pending',
  ]);

  const mapSummaryExportStatus = (raw: DwspayPaymentSummary): string | null => {
    const stripeStatus = (raw.status ?? '').toLowerCase().trim();
    if (!stripeStatus) {
      return null;
    }
    const amount = raw.amount ?? 0;
    const refunded = raw.amountRefunded ?? raw.amount_refunded ?? 0;
    if (stripeStatus === 'refunded' || (amount > 0 && refunded >= amount)) {
      return 'refunded';
    }
    if (stripeStatus === 'failed' || stripeStatus === 'payment_failed' || stripeStatus === 'cancelled'
      || stripeStatus === 'canceled' || stripeStatus === 'error') {
      return 'failed';
    }
    if (refunded > 0 && refunded < amount) {
      return 'partially_refunded';
    }
    if (
      stripeStatus === 'succeeded'
      || stripeStatus === 'captured'
      || stripeStatus === 'confirmed'
      || stripeStatus === 'partially_refunded'
    ) {
      return 'succeeded';
    }
    if (LIST_EXPORT_PENDING_STATUSES.has(stripeStatus)) {
      return 'pending';
    }
    return stripeStatus;
  };

  /** Maps GET /winesuite/payments rows to the same 18-column schema as advanced export. */
  export const mapWinesuiteSummaryToExportRow = (raw: DwspayPaymentSummary): AdvancedExportRow | null => {
    const status = mapSummaryExportStatus(raw);
    if (!status) {
      return null;
    }

    const metadata = raw.metadata ?? {};
    const paymentMethodType = raw.paymentMethodType ?? '';
    const transactionMode = paymentMethodType === 'card_present' ? 'card_present' : 'card_not_present';
    const createdSec = raw.created ? Math.floor(raw.created / 1000) : 0;
    const totalFee = raw.applicationFeeTotal ?? ((raw.commissioneWspayments ?? 0) + (raw.commissioneDivineaCom ?? 0));

    return {
      stripe_id: raw.id ?? raw.connectPaymentId ?? '',
      date: createdSec,
      status,
      amount: raw.amount ?? 0,
      amount_refunded: raw.amountRefunded ?? raw.amount_refunded ?? 0,
      currency: (raw.currency ?? 'eur').toUpperCase(),
      total_fee: totalFee,
      wspayments_fee: raw.commissioneWspayments ?? 0,
      divinea_fee: raw.commissioneDivineaCom ?? 0,
      description: raw.description ?? '',
      customer_name: metadata.customer_name ?? metadata.customerName ?? '',
      customer_email: metadata.customer_email ?? metadata.customerEmail ?? raw.customer ?? '',
      sale_id: resolveExportSaleIdFromMetadata(metadata),
      entity_type: metadata.entity_type ?? metadata.entityType ?? '',
      transaction_mode: transactionMode,
      payout_id: String(raw.payoutId ?? raw.payout_id ?? ''),
      payout_amount: raw.payoutAmount ?? raw.payout_amount ?? '',
      payout_date: raw.payoutDate ?? raw.payout_date ?? '',
    };
  };

  export const listExportColumnHeaders = (): string[] =>
    columnsForScope('payments').map((c) => tr(c.headerKey));

  export const formatExportRowAsCsvCells = (row: AdvancedExportRow): string[] =>
    columnsForScope('payments').map((c) => formatCell(row, c.field));

  /** Date range for advanced-export payout lookup from filtered list rows / query. */
  export const computeListExportPayoutLookupRange = (
    rows: AdvancedExportRow[],
    query: { dateFrom?: number; dateTo?: number },
  ): { dateFrom: string; dateToExclusive: string } => {
    const dates = rows.map((r) => r.date).filter((d) => d > 0);
    // Tight span of exported rows — not the wider list filter range (avoids huge payout lookups).
    let fromSec = dates.length ? Math.min(...dates) : query.dateFrom;
    let toSec = dates.length ? Math.max(...dates) : query.dateTo;
    if (fromSec == null) {
      fromSec = moment().subtract(365, 'days').startOf('day').unix();
    }
    if (toSec == null) {
      toSec = moment().endOf('day').unix();
    }
    const fromLocal = moment.unix(fromSec).startOf('day');
    const toExclusiveLocal = moment.unix(toSec).startOf('day').add(1, 'day');
    return {
      dateFrom: toWspaymentsUtcWallClock(fromLocal),
      dateToExclusive: toWspaymentsUtcWallClock(toExclusiveLocal),
    };
  };

  export const mergePayoutEnrichmentIntoRows = (
    targetRows: AdvancedExportRow[],
    advancedRows: AdvancedExportRow[],
  ): void => {
    const byStripeId = new Map<string, AdvancedExportRow>();
    for (const row of advancedRows) {
      const id = row?.stripe_id;
      if (id) {
        byStripeId.set(id, row);
      }
    }
    for (const row of targetRows) {
      const enriched = byStripeId.get(row.stripe_id);
      if (!enriched) {
        continue;
      }
      row.payout_id = enriched.payout_id;
      row.payout_amount = enriched.payout_amount;
      row.payout_date = enriched.payout_date;
      if (!row.sale_id?.trim() && enriched.sale_id?.trim()) {
        row.sale_id = enriched.sale_id;
      }
      if (!row.entity_type?.trim() && enriched.entity_type?.trim()) {
        row.entity_type = enriched.entity_type;
      }
    }
  };

  /** Align ngx-translate with UI locale before building export labels (avoids stale tr cache / lang drift). */
  export const prepareExportTranslations = (): void => {
    clearTranslationCache();
    const injector = (window as { injector?: unknown }).injector as import('@angular/core').Injector | undefined;
    if (!injector) {
      return;
    }
    try {
      const translate = injector.get(TranslateService);
      const lang = injector.get(DWSLanguagesService).getCurrentLanguageIso();
      if (translate.currentLang !== lang) {
        translate.use(lang);
      }
    } catch {
      // tr() will fall back to keys if services are unavailable
    }
  };

  export const buildListFilteredExportFile = (
    rows: AdvancedExportRow[],
    format: AdvancedExportFormat = 'csv',
  ): { blob: Blob; filename: string } | null => {
    prepareExportTranslations();
    const built = buildFileExport(rows, 'payments', format);
    if (!built) {
      return null;
    }
    const date = moment().format('YYYY-MM-DD');
    const ext = format === 'xlsx' ? 'xlsx' : 'csv';
    return { ...built, filename: `payments_${date}.${ext}` };
  };

  /** WS-2665 / WS-3452: always 18 columns; bonifico data comes from API when includePayout is set. */
  export const buildCsvExport = (
    rows: AdvancedExportRow[],
    exportScope: AdvancedExportScope = 'payments'
  ): { blob: Blob; filename: string } | null => {
    if (!rows?.length) {
      return null;
    }

    const columnKeys = columnsForScope(exportScope);
    const sorted = sortedExportRows(rows);
    const headers = columnKeys.map(c => tr(c.headerKey));
    const csvRows: string[] = [headers.map(escapeCsv).join(',')];

    sorted.forEach(row => {
      const line = columnKeys.map(c => escapeCsv(formatCell(row, c.field)));
      csvRows.push(line.join(','));
    });

    const csvContent = '\uFEFF' + csvRows.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return { blob, filename: buildFilename('csv') };
  };

  /** Same rows as CSV, converted to XLSX on the frontend (WS-3946). */
  export const buildXlsxExport = (
    rows: AdvancedExportRow[],
    exportScope: AdvancedExportScope = 'payments'
  ): { blob: Blob; filename: string } | null => {
    if (!rows?.length) {
      return null;
    }

    const columnKeys = columnsForScope(exportScope);
    const sorted = sortedExportRows(rows);
    const headers = columnKeys.map(c => tr(c.headerKey));
    const sheetData: (string | number)[][] = [headers];

    sorted.forEach(row => {
      sheetData.push(columnKeys.map(c => formatXlsxCell(row, c.field)));
    });

    const sheet = utils.aoa_to_sheet(sheetData);
    const moneyColIndexes = columnKeys
      .map((c, index) => (MONEY_FIELDS.has(c.field) ? index : -1))
      .filter((index) => index >= 0);
    applyMonetaryNumberFormat(sheet, moneyColIndexes);
    sheet['!cols'] = columnKeys.map(() => ({ wch: 18 }));

    const book = utils.book_new();
    utils.book_append_sheet(book, sheet, 'WS Payments');
    const buffer = write(book, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return { blob, filename: buildFilename('xlsx') };
  };

  export const buildFileExport = (
    rows: AdvancedExportRow[],
    exportScope: AdvancedExportScope = 'payments',
    format: AdvancedExportFormat = 'csv'
  ): { blob: Blob; filename: string } | null => {
    return format === 'xlsx'
      ? buildXlsxExport(rows, exportScope)
      : buildCsvExport(rows, exportScope);
  };

  export const exportToCSV = (rows: AdvancedExportRow[], exportScope: AdvancedExportScope = 'payments'): void => {
    const built = buildCsvExport(rows, exportScope);
    if (!built) {
      return;
    }

    const url = URL.createObjectURL(built.blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = built.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };
}
